/**
 * Parses the ABC cyclone warning feed format into GeoJSON.
 * Converts forecast_points (track + positions) and hazards (warning area polygons).
 */

/**
 * Parse coordinate strings like "13.4S" -> -13.4, "148.7E" -> 148.7
 */
function parseLatLngString(str) {
  if (typeof str !== "string") return 0;
  const match = str.trim().match(/^([-\d.]+)([NSEW])?$/i);
  if (!match) return parseFloat(str) || 0;
  let value = parseFloat(match[1]);
  const dir = (match[2] || "").toUpperCase();
  if (dir === "S" || dir === "W") value = -value;
  return value;
}

/**
 * Get coordinate value - handles both numeric (pre-converted) and string formats
 */
function toCoord(value) {
  if (typeof value === "number" && !Number.isNaN(value)) return value;
  if (typeof value === "string") return parseLatLngString(value);
  return 0;
}

/**
 * Parse WKT POLYGON or MULTIPOLYGON to GeoJSON coordinates.
 * WKT format: "lng lat" (space-separated), GeoJSON: [lng, lat]
 */
function parseWktToGeoJson(wkt) {
  if (!wkt || typeof wkt !== "string") return [];

  const polygons = [];
  const coordRegex = /(-?\d+\.?\d*)\s+(-?\d+\.?\d*)/g;

  // MULTIPOLYGON (((a b, c d)), ((e f, g h))) or POLYGON ((a b, c d))
  const isMulti = wkt.toUpperCase().startsWith("MULTIPOLYGON");
  const inner = wkt.replace(/^(MULTIPOLYGON|POLYGON)\s*/i, "").trim();

  const polygonParts = isMulti ? inner.split(")), ((") : [inner];

  for (const part of polygonParts) {
    const coords = [];
    const cleanPart = part.replace(/^\(+|\)+$/g, "");
    coordRegex.lastIndex = 0;
    let m;
    while ((m = coordRegex.exec(cleanPart)) !== null) {
      coords.push([parseFloat(m[1]), parseFloat(m[2])]);
    }
    if (coords.length >= 3) {
      // Close the ring (GeoJSON requires first point = last point)
      if (
        coords[0][0] !== coords[coords.length - 1][0] ||
        coords[0][1] !== coords[coords.length - 1][1]
      ) {
        coords.push(coords[0]);
      }
      polygons.push([coords]);
    }
  }

  return polygons;
}

/**
 * Convert ABC feed JSON to GeoJSON FeatureCollection
 * @param {Object} feed - The raw ABC feed (from sample.json structure)
 * @returns {Object} GeoJSON FeatureCollection
 */
export function parseAbcFeed(feed) {
  const features = [];

  // 1. Forecast points -> Point features + LineString track
  const forecastPoints = feed.forecast_points || [];
  const trackCoords = [];

  for (const pt of forecastPoints) {
    const lng = toCoord(pt.longitude);
    const lat = toCoord(pt.latitude);
    const coord = [lng, lat];
    trackCoords.push(coord);

    const isCurrent = pt.plus_hours === 0;

    features.push({
      type: "Feature",
      geometry: { type: "Point", coordinates: coord },
      properties: {
        plus_hours: pt.plus_hours,
        date: pt.date,
        category_name: pt.category_name,
        position_acc_km: pt.position_acc_km,
        fixtype: isCurrent ? "Current" : "Forecast",
      },
    });
  }

  // Add LineString for the track path
  if (trackCoords.length >= 2) {
    features.push({
      type: "Feature",
      geometry: { type: "LineString", coordinates: trackCoords },
      properties: { tracktype: "Forecast" },
    });
  }

  // 1b. Historic points -> Point features (grey)
  const historicPoints = feed.historic || [];
  const historicTrackCoords = [];
  for (const pt of historicPoints) {
    const lng = toCoord(pt.longitude);
    const lat = toCoord(pt.latitude);
    const coord = [lng, lat];
    historicTrackCoords.push(coord);
    features.push({
      type: "Feature",
      geometry: { type: "Point", coordinates: coord },
      properties: {
        plus_hours: pt.plus_hours,
        date: pt.date,
        category_name: pt.category_name,
        position_acc_km: pt.position_acc_km,
        fixtype: "Historic",
      },
    });
  }
  if (historicTrackCoords.length >= 2) {
    features.push({
      type: "Feature",
      geometry: { type: "LineString", coordinates: historicTrackCoords },
      properties: { tracktype: "Historic" },
    });
  }

  // Connector line from last historic to first forecast
  if (
    historicTrackCoords.length >= 1 &&
    trackCoords.length >= 1
  ) {
    const lastHistoric = historicTrackCoords[historicTrackCoords.length - 1];
    const firstForecast = trackCoords[0];
    features.push({
      type: "Feature",
      geometry: {
        type: "LineString",
        coordinates: [lastHistoric, firstForecast],
      },
      properties: { tracktype: "Connector" },
    });
  }

  // 2. Hazards -> Polygon features from warning_area_polygons
  const hazards = feed.hazards || [];
  for (const hazard of hazards) {
    // Support both top-level and texts.warning_area_polygons
    const wkt =
      hazard.warning_area_polygons ?? hazard.texts?.warning_area_polygons;
    if (!wkt || !String(wkt).trim()) continue;

    const polygonRings = parseWktToGeoJson(wkt);
    const isUnk = (hazard.urgency || "").toUpperCase() === "UNK";
    for (const ring of polygonRings) {
      features.push({
        type: "Feature",
        geometry: { type: "Polygon", coordinates: ring },
        properties: {
          severity: hazard.severity,
          urgency: hazard.urgency,
          type: hazard.type,
          warning_areas:
            hazard.warning_areas ?? hazard.texts?.warning_areas ?? "",
        },
      });
      if (isUnk && ring[0] && ring[0].length >= 2) {
        features.push({
          type: "Feature",
          geometry: { type: "LineString", coordinates: ring[0] },
          properties: { hazard_boundary: true, urgency: "UNK" },
        });
      }
    }
  }

  return {
    type: "FeatureCollection",
    features,
    properties: {
      cyclone_name: feed.cyclone_name,
      issue_time_utc: feed["issue-time-utc"],
      intensity: feed.intensity,
    },
  };
}

/**
 * Compute bearing in degrees from point A to B (0 = North, 90 = East).
 */
function getBearing(lat1, lng1, lat2, lng2) {
  const φ1 = (lat1 * Math.PI) / 180;
  const φ2 = (lat2 * Math.PI) / 180;
  const Δλ = ((lng2 - lng1) * Math.PI) / 180;
  const y = Math.sin(Δλ) * Math.cos(φ2);
  const x = Math.cos(φ1) * Math.sin(φ2) - Math.sin(φ1) * Math.cos(φ2) * Math.cos(Δλ);
  return ((Math.atan2(y, x) * 180) / Math.PI + 360) % 360;
}

/**
 * Get the interpolated "current" cyclone position based on time elapsed since forecast.
 * Uses the first forecast point's date as base (plus_hours 0).
 * @param {Object} feed - The raw ABC feed
 * @returns {{ lng: number, lat: number, bearing: number } | null}
 */
export function getCurrentPosition(feed) {
  const points = feed.forecast_points || [];
  if (points.length === 0) return null;

  const firstPoint = points[0];
  const baseTime = new Date(firstPoint.date).getTime();
  const now = Date.now();
  const hoursSinceBase = (now - baseTime) / (1000 * 60 * 60);

  if (hoursSinceBase <= 0) {
    const bearing =
      points.length >= 2
        ? getBearing(
            toCoord(firstPoint.latitude),
            toCoord(firstPoint.longitude),
            toCoord(points[1].latitude),
            toCoord(points[1].longitude),
          )
        : 0;
    return {
      lng: toCoord(firstPoint.longitude),
      lat: toCoord(firstPoint.latitude),
      bearing,
    };
  }

  const lastPoint = points[points.length - 1];
  if (hoursSinceBase >= lastPoint.plus_hours) {
    const bearing =
      points.length >= 2
        ? getBearing(
            toCoord(points[points.length - 2].latitude),
            toCoord(points[points.length - 2].longitude),
            toCoord(lastPoint.latitude),
            toCoord(lastPoint.longitude),
          )
        : 0;
    return {
      lng: toCoord(lastPoint.longitude),
      lat: toCoord(lastPoint.latitude),
      bearing,
    };
  }

  let prev = firstPoint;
  let next = firstPoint;
  for (let i = 0; i < points.length - 1; i++) {
    if (points[i].plus_hours <= hoursSinceBase && hoursSinceBase < points[i + 1].plus_hours) {
      prev = points[i];
      next = points[i + 1];
      break;
    }
  }

  const bearing = getBearing(
    toCoord(prev.latitude),
    toCoord(prev.longitude),
    toCoord(next.latitude),
    toCoord(next.longitude),
  );
  const t = (hoursSinceBase - prev.plus_hours) / (next.plus_hours - prev.plus_hours);
  return {
    lng: toCoord(prev.longitude) + t * (toCoord(next.longitude) - toCoord(prev.longitude)),
    lat: toCoord(prev.latitude) + t * (toCoord(next.latitude) - toCoord(prev.latitude)),
    bearing,
  };
}
